import { ChevronRightIcon, Shield } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router';

import { HeroPatternNew } from '@/components/hero-pattern';
import { Button } from '@/components/ui/button';
import { TypographyMuted } from '@/components/ui/typography';

import { CalendlyWidget } from '@/lib/calendly/calendly.widget';
import {
  Navigation,
  SectionFooter,
  SectionReadyToRelease,
  SignInOrDashboard,
} from '@/pages/unauthenticated/components';
import { pricing } from '@/pages/unauthenticated/pricing/components/constants';
import { SectionTierComparison } from '@/pages/unauthenticated/pricing/components/sections/section.tier-comparison';
import { TierCard } from '@/pages/unauthenticated/pricing/components/tier-card';

export default function PricingPage() {
  return (
    <>
      <Navigation>
        <SignInOrDashboard />
      </Navigation>
      <main>
        <HeroPatternNew className="isolate lg:top-132" />
        <section className="redesign-container">
          <div className="mx-auto max-w-7xl px-0 py-24 pb-10 sm:py-32 sm:pb-20 lg:flex lg:items-center lg:gap-x-10 lg:px-8 lg:pt-30 lg:pb-20 xl:lg:pb-16">
            <div className="mx-auto max-w-xl lg:mx-0 lg:flex-auto">
              <div className="flex">
                <div className="relative flex items-center gap-x-4 rounded-full bg-halfmast-1000 px-4 py-1 text-sm/6 text-muted-foreground ring-1 ring-gray-900/10 hover:ring-gray-900/20">
                  <span className="font-semibold text-primary">
                    What&#39;s new
                  </span>
                  <span
                    aria-hidden="true"
                    className="h-4 w-px bg-halfmast-800"
                  />
                  <a
                    href="https://github.com/flagsync/cli"
                    className="flex items-center gap-x-1"
                  >
                    <span aria-hidden="true" className="absolute inset-0" />
                    Just shipped FlagSync CLI
                    <ChevronRightIcon
                      aria-hidden="true"
                      className="-mr-2 size-5 text-halfmast-800"
                    />
                  </a>
                </div>
              </div>
              <h1 className="mt-10 text-5xl font-black tracking-tight text-pretty sm:text-7xl max-w-[10ch]">
                Less cash, more{' '}
                <span className="italic text-primary">flags</span>
              </h1>
              <p className="mt-8 text-lg font-medium text-pretty text-muted-foreground sm:text-xl/8">
                Enjoy a 14-day free trial on all plans, no credit card required.
                Upgrade anytime with no minimum seat requirement.
              </p>
              <div className="mt-10 flex flex-col md:flex-row items-center gap-y-2 md:gap-x-6">
                <Button
                  asChild
                  size="lg"
                  variant="glowing"
                  className="uppercase w-full md:w-fit flex-shrink-0"
                >
                  <Link to="/register">Try FlagSync for Free</Link>
                </Button>
              </div>
              <TypographyMuted className="inline-flex items-center gap-2 mt-6">
                <Shield className="size-4" />
                <span>No credit card required • Free 14-day trial</span>
              </TypographyMuted>
            </div>
            <div className="hidden mt-16 sm:mt-24 lg:mt-0 lg:shrink-0 lg:grow lg:h-[630px] xl:flex items-center">
              <img
                loading="lazy"
                width={700}
                height={630}
                src="/img/dashboard.webp"
                alt="dashboard image"
              />
            </div>
          </div>
        </section>

        <section className="redesign-container py-12 md:py-24">
          <div className="mx-auto grid max-w-md grid-cols-1 gap-8 lg:mx-0 lg:max-w-none lg:grid-cols-3">
            {pricing.tiers.map((tier) => (
              <TierCard key={tier.activeTier} tier={tier} />
            ))}
          </div>
        </section>

        <SectionTierComparison />
        <SectionReadyToRelease />
        <SectionFooter />
        <CalendlyWidget />
      </main>
    </>
  );
}
